import { Injectable, signal } from '@angular/core';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  /** Optional action (e.g. "Erneut versuchen"); the toast closes when it is used. */
  action?: { label: string; run: () => void };
}

/** How long a toast stays: errors linger, as they usually need to be read twice. */
const DURATION_MS: Record<ToastKind, number> = { success: 4000, info: 5000, error: 8000 };
const MAX_VISIBLE = 4;

/**
 * Short-lived confirmations and errors that are not tied to one spot on the page. Writes answer
 * 202 and are only confirmed by an event later, so the page that sent them cannot always show
 * the outcome next to the button (the dialog is closed, the row is gone, the user navigated
 * away). The host component (`ToastHost`) renders the list in an aria-live region.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private nextId = 1;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  private readonly _toasts = signal<Toast[]>([]);
  readonly toasts = this._toasts.asReadonly();

  success(message: string): number { return this.show('success', message); }

  info(message: string): number { return this.show('info', message); }

  error(message: string, action?: Toast['action']): number { return this.show('error', message, action); }

  dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  private show(kind: ToastKind, message: string, action?: Toast['action']): number {
    // The same message twice in a row (a double click, a retry loop) is one toast, not a stack.
    const existing = this._toasts().find((t) => t.kind === kind && t.message === message);
    if (existing) { this.dismiss(existing.id); }

    const id = this.nextId++;
    this._toasts.update((list) => [...list, { id, kind, message, action }].slice(-MAX_VISIBLE));
    this.timers.set(id, setTimeout(() => this.dismiss(id), DURATION_MS[kind]));
    return id;
  }
}
